import { IAddressDto, IDawaAddressDto } from './dtos';
import { IAddress, IAddressPrediction, IPickUpKeyAddress } from './models';

export function mapAddressFromDawaDto({
  adresse: {
    id,
    vejnavn: street,
    husnr: houseNumber,
    etage: floor,
    dør: appartmentNumber,
    postnr: postNumber,
    postnrnavn: city,
    supplerendebynavn: area,
    x: longitude,
    y: latitude,
  },
  tekst: full,
}: IDawaAddressDto): IAddress {
  return {
    id,
    street,
    houseNumber,
    floor,
    appartmentNumber,
    postNumber,
    city,
    full,
    area,
    longitude,
    latitude,
  };
}

export function mapAddressPredictionFromDawaDto(dto: IDawaAddressDto): IAddressPrediction {
  return {
    id: dto.adresse.id,
    text: dto.tekst,
    __internal_address: mapAddressFromDawaDto(dto),
  };
}

export function mapAddressFromGoogleDto({
  place_id,
  formatted_address,
  address_components,
}: google.maps.GeocoderResult): IAddress | undefined {
  const street = address_components.find(({ types }) => types.includes('route'));
  const houseNumber = address_components.find(({ types }) => types.includes('street_number'));
  const postNumber = address_components.find(({ types }) => types.includes('postal_code'));
  const city =
    address_components.find(({ types }) => types.includes('sublocality')) ??
    address_components.find(({ types }) => types.includes('postal_town')) ??
    address_components.find(({ types }) => types.includes('locality'));

  return street && houseNumber && postNumber && city
    ? {
        id: place_id,
        full: formatted_address,
        street: street.long_name,
        houseNumber: houseNumber.long_name,
        postNumber: postNumber.long_name,
        city: city.long_name,
      }
    : undefined;
}

export function mapAddressPredictionFromGoogleDto({
  place_id,
  description,
  types,
}: google.maps.places.AutocompletePrediction): IAddressPrediction | undefined {
  return types.includes('street_address') || types.includes('premise')
    ? {
        id: place_id,
        text: description,
      }
    : undefined;
}

export function mapFullAddressFromDto(dto: IAddressDto): string {
  let output = '';

  if (dto.street) {
    output += `${dto.street} `;
  }

  if (dto.buildingNumber) {
    output += `${dto.buildingNumber}, `;
  }

  if (dto.buildingFloor) {
    output += `${dto.buildingFloor}, `;
  }

  if (dto.buildingDoor) {
    output += `${dto.buildingDoor}, `;
  }

  if (dto.cityArea) {
    output += `${dto.cityArea}, `;
  }

  if (dto.zip) {
    output += `${dto.zip} `;
  }

  if (dto.city) {
    output += `${dto.city} `;
  }

  if (dto.country) {
    output += `${dto.country}`;
  }

  return output.trim();
}

export function mapAddressPredictionFromGoogleVersion2(
  details: google.maps.places.PlaceResult
): IAddressPrediction | null {
  const components = details.address_components || [];

  const getField = (type: string): string =>
    components.find(component => component.types.includes(type))?.long_name || '';

  return {
    id: details.place_id ?? '',
    text: details.formatted_address ?? '',
    __internal_address: {
      id: details.place_id ?? '',
      full: details.formatted_address ?? '',
      street: getField('route'),
      houseNumber: getField('street_number'),
      floor: getField('floor'),
      appartmentNumber: getField('subpremise'),
      postNumber: getField('postal_code'),
      city: getField('postal_town'),
      area: getField('sublocality'),
      longitude: details.geometry?.location?.lng(),
      latitude: details.geometry?.location?.lat(),
    },
  };
}

export function mapAddressKeyPickUp(address: IPickUpKeyAddress): IAddress {
  // line 1 is concatenating Street and House Number
  // line 2 is concatenating Floor and apartment number
  const firstAddressLine = address?.line1?.split(' ') ?? [];
  const secondAddressLine = address?.line2?.split(' ') ?? [];

  return {
    id: address?.id ?? '',
    city: address?.city ?? '',
    full: address?.text ?? '',
    street: firstAddressLine.slice(0, -1).join(' ') ?? '',
    houseNumber: firstAddressLine?.[firstAddressLine?.length - 1] ?? '',
    floor: secondAddressLine?.[0] ?? '',
    appartmentNumber: secondAddressLine?.[secondAddressLine?.length - 1] ?? '',
    postNumber: address?.postalCode ?? '',
    longitude: address?.longitude,
    latitude: address?.latitude,
    area: address?.stateOrProvince ?? '',
  };
}
